// Allow observing the scroll progress in an element
//
// Example: x-scroll-progress="console.log($scrollOffset, $height)"

/**
 * @param {import('alpinejs')} Alpine
 */
export default Alpine => {
  Alpine.directive(
    'scroll-progress',
    (el, { expression, modifiers: rawModifiers, value }, { evaluateLater, cleanup }) => {
      const modifiers = new Map()
      for (let i = 0; i < rawModifiers.length; i++) {
        const key = rawModifiers[i]

        if (
          [
            'margin-top-min',
            'margin-top-max',
            'margin-top-element',
            'margin-top-viewport',
            'margin-bottom-min',
            'margin-bottom-max',
            'margin-bottom-element',
            'margin-bottom-viewport',
          ].includes(key)
        ) {
          const value = (Number(rawModifiers[i + 1] ?? 0) || 0) / 100

          if (value !== 0) {
            modifiers.set(key, value)
          }
          i++
        } else {
          modifiers.set(key, true)
        }
      }

      const getNumericModifier = (key, defaultValue = 0) =>
        modifiers.has(key) ? modifiers.get(key) : defaultValue
      const marginTopMinFraction = getNumericModifier('margin-top-min')
      const marginBottomMinFraction = getNumericModifier('margin-bottom-min')
      const marginTopMaxFraction = getNumericModifier('margin-top-max')
      const marginBottomMaxFraction = getNumericModifier('margin-bottom-max')
      const marginTopElementFraction = getNumericModifier('margin-top-element')
      const marginBottomElementFraction = getNumericModifier('margin-bottom-element')
      const marginTopViewportFraction = getNumericModifier('margin-top-viewport')
      const marginBottomViewportFraction = getNumericModifier('margin-bottom-viewport')

      const clamp = !modifiers.has('no-clamp')
      const expensivePositionCalculation = modifiers.has('expensive-position-calculation')

      // --------------------------------- //

      //
      // DOM Interactions
      //

      // --------------------------------- //

      //
      // Window size
      // The size of the viewport
      //

      // Declare state
      const windowSizeState = Alpine.reactive({
        value: getWindowSize(),
      })

      // Get value
      function getWindowSize() {
        return {
          width: window.innerWidth,
          height: window.innerHeight,
        }
      }

      // Update value
      function updateWindowSize() {
        windowSizeState.value = getWindowSize()
      }

      function windowResizeListener() {
        updateWindowSize()
      }

      // Watch window size
      function attachWindowSizeListener() {
        detachWindowSizeListener()
        window.addEventListener('resize', windowResizeListener)
      }

      // Detach listener
      function detachWindowSizeListener() {
        window.removeEventListener('resize', windowResizeListener)
      }

      // --------------------------------- //

      //
      // Element size
      // The size of the element
      //

      const rect = el.getBoundingClientRect()

      // Declare state
      const elementSizeState = Alpine.reactive({
        value: getElementSize(),
      })

      // Get value
      function getElementSize({ width, height } = el.getBoundingClientRect()) {
        return { width, height }
      }

      // Update value
      function updateElementSize(size) {
        const newSize = getElementSize(size)
        if (
          newSize.width !== elementSizeState.value.width ||
          newSize.height !== elementSizeState.value.height
        ) {
          elementSizeState.value = newSize
        }
      }

      // Attach listener
      let resizeObserver
      function attachElementSizeListener() {
        detachElementSizeListener()

        resizeObserver = new ResizeObserver(entries => {
          for (const entry of entries) {
            updateElementSize({
              width: entry.borderBoxSize[0].inlineSize,
              height: entry.borderBoxSize[0].blockSize,
            })
          }
        })
        resizeObserver.observe(el)
      }

      // Detach listener
      function detachElementSizeListener() {
        resizeObserver?.disconnect()
      }

      // --------------------------------- //

      //
      // Scroll position
      // The distance from the top of the document to the top of the viewport
      //

      // Declare state
      const scrollPosition = Alpine.reactive({
        value: getScrollY(),
      })

      // Get value
      function getScrollY() {
        return Math.max(window.scrollY, 0)
      }

      // Update value
      function updateScrollY() {
        scrollPosition.value = getScrollY()
      }

      function scrollListener() {
        updateScrollY()

        if (expensivePositionCalculation) {
          updateScrollOffsetState()
        }
      }

      // Attach listener
      function attachScrollListener() {
        detachScrollListener()
        // Watch scroll position
        window.addEventListener('scroll', scrollListener, { passive: true })
      }

      // Detach listener
      function detachScrollListener() {
        window.removeEventListener('scroll', scrollListener)
      }

      // --------------------------------- //

      //
      // Scroll offset
      // The distance from the top of the element to the top of the document
      //

      // Declare state
      const scrollOffsetState = Alpine.reactive({
        value: getScrollOffsetState(),
      })

      // Get value
      function getScrollOffsetState() {
        return el.getBoundingClientRect().top + scrollPosition.value
      }

      // Update value
      const updateScrollOffsetState = Alpine.debounce(function updateScrollOffsetState() {
        scrollOffsetState.value = el.getBoundingClientRect().top + scrollPosition.value
      }, 0)

      function scrollOffsetListener() {
        updateScrollOffsetState()
      }

      // Attach listener
      function attachScrollOffsetListener() {
        if (!expensivePositionCalculation) return

        detachScrollOffsetListener()

        // Watch scroll position
        window.addEventListener('scroll', scrollOffsetListener, { passive: true })
      }

      // Detach listener
      function detachScrollOffsetListener() {
        window.removeEventListener('scroll', scrollOffsetListener)
      }

      // --------------------------------- //

      // Attach listeners
      function attachAllListeners() {
        attachWindowSizeListener()
        attachElementSizeListener()
        attachScrollListener()
        attachScrollOffsetListener()
      }

      // Detach listeners
      function detachAllListeners() {
        detachWindowSizeListener()
        detachElementSizeListener()
        detachScrollListener()
        detachScrollOffsetListener()
      }

      // --------------------------------- //

      //
      // State
      //

      // --------------------------------- //

      // Recalculate the scroll offset when the element or window size changes
      Alpine.effect(() => {
        // Reference all reactive properties to trigger the effect
        ;(() => [
          windowSizeState.value,
          elementSizeState.value.width,
          elementSizeState.value.height,
        ])()

        updateScrollOffsetState()
      })

      attachAllListeners()

      const outputState = Alpine.reactive({
        get progress() {
          const elementHeight = elementSizeState.value.height
          const windowHeight = windowSizeState.value.height

          const marginTopOffset =
            marginTopMinFraction * Math.min(elementHeight, windowHeight) +
            marginTopMaxFraction * Math.max(elementHeight, windowHeight) +
            marginTopElementFraction * elementHeight +
            marginTopViewportFraction * windowHeight
          const marginBottomOffset =
            marginBottomMinFraction * Math.min(elementHeight, windowHeight) +
            marginBottomMaxFraction * Math.max(elementHeight, windowHeight) +
            marginBottomElementFraction * elementHeight +
            marginBottomViewportFraction * windowHeight
          const maxScroll = elementSizeState.value.height + marginTopOffset + marginBottomOffset
          const currentScroll =
            windowSizeState.value.height +
            scrollPosition.value -
            scrollOffsetState.value +
            marginTopOffset
          const scrollProgress = currentScroll / maxScroll
          return clamp
            ? Math.round(Math.min(Math.max(scrollProgress, 0), 1) * 10 ** 3) / 10 ** 3
            : scrollProgress
        },
      })

      Alpine.addScopeToNode(el, {
        get $scrollProgress() {
          return outputState.progress
        },
      })

      const runEffect = expression.length > 0 ? evaluateLater(expression) : () => {}
      Alpine.effect(() => {
        runEffect()
      })

      cleanup(() => {
        detachAllListeners()
      })
    },
  )
}
